import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { AgenturModel } from 'app/models/agentur.model';
import { useStore } from 'app/context';
import Widget from 'app/components/common/Widget';
import { formatDate, formatNum } from 'app/utils';
import { AkumaCampaignModel } from 'app/stores/akuma.campaign.store';
import { IAgtAkumaCampaignDetailModel } from 'app/stores/agt.akumaaktionen.store';

interface IJpgAkumaAktionenWidget {
	agt: AgenturModel;
	year: number;
	campaignIds: number[];
}

interface ICampaignDisplayData {
	campaign: AkumaCampaignModel;
	details?: IAgtAkumaCampaignDetailModel;
	hasData: boolean;
}

export const JpgAkumaAktionenWidget = observer((props: IJpgAkumaAktionenWidget) => {
	const { agtAkumaAktionenUiStore } = useStore();
	const [campaigns, setCampaigns] = useState<ICampaignDisplayData[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		loadCampaignData();
	}, [props.campaignIds, props.agt.agtId, props.year]);

	const loadCampaignData = async () => {
		setLoading(true);
		try {
			// Ensure stores are loaded
			await agtAkumaAktionenUiStore.load();

			// Get campaigns from the UI store (already loaded during _baseLoad)
			const allCampaigns = agtAkumaAktionenUiStore.campaigns;

			// Get unique campaign IDs
			const uniqueCampaignIds = Array.from(new Set(props.campaignIds));

			const campaignDataPromises = uniqueCampaignIds.map(async (campaignId) => {
				// Get campaign metadata
				const campaign = allCampaigns.find((c: AkumaCampaignModel) => c.id === campaignId);
				if (!campaign) {
					return null;
				}

				// Get campaign details for this agency
				try {
					const maxKw = agtAkumaAktionenUiStore.maxKw;
					const detailsData = await agtAkumaAktionenUiStore.agtAkumaAktionenStore.getCampaignDetailsByAgency(campaignId, maxKw);
					const details = detailsData.find((d) => d.agtid === props.agt.agtId);

					return {
						campaign,
						details,
						hasData: !!details && details.anzahlaktionstreffer > 0,
					};
				} catch (error) {
					return {
						campaign,
						details: undefined,
						hasData: false,
					};
				}
			});

			const results = await Promise.all(campaignDataPromises);
			const validCampaigns = results.filter((c) => c !== null) as ICampaignDisplayData[];

			setCampaigns(validCampaigns);
		} catch (error) {
			console.error('Error loading campaign data:', error);
		} finally {
			setLoading(false);
		}
	};

	if (loading) {
		return (
			<Widget title="Akuma Aktionen" disableCollapse={true}>
				<div className="pad-1rem">Aktionen werden geladen...</div>
			</Widget>
		);
	}

	if (campaigns.length === 0) {
		return (
			<Widget title="Akuma Aktionen" disableCollapse={true}>
				<div className="pad-1rem">Keine Aktionen gefunden</div>
			</Widget>
		);
	}

	return (
		<div className="table-container is-boxed">
			<table className="table last-col-right no-row-click">
				<thead>
					<tr>
						<th>ID</th>
						<th>Aktion</th>
						<th>Tags</th>
						<th>End Datum</th>
						<th>Aktionstreffer</th>
					</tr>
				</thead>
				<tbody>
					{campaigns.map((campaignData, index) => (
						<CampaignTableRow key={`${campaignData.campaign.id}-${index}`} data={campaignData} />
					))}
				</tbody>
			</table>
		</div>
	);
});

interface ICampaignTableRow {
	data: ICampaignDisplayData;
}

const CampaignTableRow = observer((props: ICampaignTableRow) => {
	const { campaign, details, hasData } = props.data;

	return (
		<tr>
			<td>
				<strong>{campaign.id}</strong>
			</td>
			<td>
				<strong>{campaign.campaignName}</strong>
			</td>
			<td>
				<div className="tags">
					{campaign.campaignTags.map((tag, idx) => (
						<span key={idx} className={`tag is-small is-${tag.color}`}>
							{tag.text}
						</span>
					))}
				</div>
			</td>
			<td>{formatDate(campaign.campaignEndDate)}</td>
			{hasData && details ? (
				<td className="has-text-right">{formatNum(details.anzahlaktionstreffer)}</td>
			) : (
				<td className="has-text-centered">
					<span className="color-grey">Keine Daten</span>
				</td>
			)}
		</tr>
	);
});
